/**
 * TestAndLinkKeyDialog — paste a personal API key, probe it via /api/probe-key,
 * and on success persist it to the userApiKeys store slice (which already
 * mirrors to users/{uid}/settings/apiKeys).
 *
 * The key never leaves the dialog except via the probe POST and the store
 * setter — no console logging, no telemetry.
 */
import React, { useState } from 'react';
import { useStore } from '../../store';
import { auth } from '../../firebase';
import type { ApiProviderDef } from '../../config/apiProviders';
import { X, ExternalLink, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  provider: ApiProviderDef;
  open: boolean;
  onClose: () => void;
  /** Called after the key is saved. Parent typically marks the gap resolved. */
  onLinked: () => void;
}

interface ProbeResponse {
  ok: boolean;
  status?: number;
  message?: string;
}

export const TestAndLinkKeyDialog: React.FC<Props> = ({ provider, open, onClose, onLinked }) => {
  const userApiKeys = useStore(s => s.userApiKeys);
  const setUserApiKeys = useStore(s => s.setUserApiKeys);
  const [keyInput, setKeyInput] = useState('');
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<ProbeResponse | null>(null);

  if (!open) return null;

  const reset = () => {
    setKeyInput('');
    setProbeResult(null);
    setProbing(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const probe = async () => {
    setProbing(true);
    setProbeResult(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Not signed in');
      const res = await fetch('/api/probe-key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ providerId: provider.id, key: keyInput }),
      });
      const json = (await res.json()) as ProbeResponse;
      setProbeResult(json);
    } catch (e) {
      setProbeResult({ ok: false, message: (e as Error).message });
    } finally {
      setProbing(false);
    }
  };

  const save = () => {
    if (!probeResult?.ok) return;
    setUserApiKeys({ ...userApiKeys, [provider.userKeyField]: keyInput });
    toast.success(`${provider.displayName} key linked`);
    onLinked();
    handleClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={handleClose}
    >
      <div
        className="bg-background w-[calc(100vw-2rem)] max-w-[480px] max-h-[90vh] overflow-y-auto shadow-2xl border border-border"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">
              Link your {provider.displayName} key
            </h2>
            <p className="text-[10px] text-text-secondary mt-0.5">
              Provide your own key for this provider. We'll test it before linking.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-text-muted hover:text-text-primary"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <a
            href={provider.signupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-primary uppercase tracking-widest inline-flex items-center gap-1 hover:underline"
          >
            Request a free key <ExternalLink className="w-3 h-3" />
          </a>

          <label className="block">
            <span className="type-body font-bold uppercase tracking-widest text-text-secondary">
              {provider.displayName} key
            </span>
            <input
              type="password"
              value={keyInput}
              onChange={e => {
                setKeyInput(e.target.value);
                if (probeResult) setProbeResult(null);
              }}
              placeholder={`Paste your ${provider.displayName} key`}
              autoComplete="off"
              spellCheck={false}
              className="mt-1 w-full px-2 py-1.5 bg-canvas border border-border text-text-primary font-mono text-[11px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
            />
          </label>

          {probeResult && (
            <div
              className={`type-section flex items-start gap-1.5 px-2 py-1.5 border ${
                probeResult.ok
                  ? 'text-success border-success/40 bg-success/10'
                  : 'text-danger border-danger/40 bg-danger/10'
              }`}
            >
              {probeResult.ok ? (
                <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
              ) : (
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              )}
              <span className="font-mono normal-case tracking-normal">
                {probeResult.ok ? 'Key works' : probeResult.message ?? 'Probe failed'}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <button
            onClick={handleClose}
            className="type-section text-text-secondary hover:text-text-primary px-3 py-1.5 border border-border hover:bg-state-hover"
          >
            Cancel
          </button>
          <button
            onClick={probe}
            disabled={!keyInput || probing}
            className="type-section text-text-secondary hover:text-text-primary px-3 py-1.5 border border-border hover:bg-state-hover disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            {probing && <Loader2 className="w-3 h-3 animate-spin" />}
            {probing ? 'Testing…' : 'Test'}
          </button>
          <button
            onClick={save}
            disabled={!probeResult?.ok}
            className="type-section text-white bg-primary hover:bg-primary/90 px-3 py-1.5 border border-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Link Key
          </button>
        </div>
      </div>
    </div>
  );
};
